import { useTranslation } from "react-i18next";
import WeatherGlyph from "@/components/WeatherGlyph";

/**
 * Affiliate network → user-facing display name. The pill label reflects
 * where the user lands on click, not the upstream data cache. Add new
 * networks here when we onboard them; unknown values fall through to a
 * generic "Live · Travelpayouts" so the pill never breaks.
 */
const NETWORK_LABELS = {
    goindigo: "GoIndigo",
    travelpayouts: "Travelpayouts",
    awin: "Awin",
    viator: "Viator",
    klook: "Klook",
};

function altDisplayLabel(altLabel) {
    if (!altLabel) return null;
    // The backend emits e.g. "+1 via travelpayouts" — convert the trailing
    // network slug into the friendly label so the subtext is consistent.
    const m = /^\+(\d+)\s+via\s+(.+)$/i.exec(altLabel);
    if (!m) return altLabel;
    const display = NETWORK_LABELS[m[2].toLowerCase()] || m[2];
    return `+${m[1]} via ${display}`;
}

/**
 * Card-header source pill.
 *
 * Resolution order (Phase 8L):
 *   1. ``provider === "singapore_airlines"`` → "Live · Singapore Airlines (sandbox)".
 *   2. ``source === "web_search"``           → "Web · {label}".
 *   3. Recognised ``affiliate_network``      → "Live · {Network}" (this beats
 *      ``source === "seed"`` so seed-fed IN-domestic flights still show
 *      "Live · GoIndigo" instead of the neutral "Demo data" pill).
 *   4. ``source === "seed"``                 → neutral "Demo data" pill.
 *   5. fallback                              → "Live · Travelpayouts".
 */
export function SourcePill({
    source = "travelpayouts",
    source_label = "Travelpayouts",
    affiliate_network,
    alt_network_label,
}) {
    const { t } = useTranslation();
    const grounded = source === "web_search";
    const sia = source === "singapore_airlines";
    const net = (affiliate_network || "").toLowerCase();
    const friendly = NETWORK_LABELS[net];
    const isSeedNeutral = source === "seed" && !friendly;

    let label;
    let pillTestId;
    let dotInk;
    if (sia) {
        label = t("cards.source_sia_sandbox");
        pillTestId = "sia-source-pill";
        dotInk = false;
    } else if (grounded) {
        label = `Web · ${source_label}`;
        pillTestId = "web-source-pill";
        dotInk = true;
    } else if (friendly) {
        label = `Live · ${friendly}`;
        pillTestId = "live-pill";
        dotInk = false;
    } else if (isSeedNeutral) {
        label = t("cards.source_seed_pill");
        pillTestId = "seed-source-pill";
        dotInk = true;
    } else {
        // `raw`/unknown → preserve the prior default so we never render
        // a bare "Live" or expose the sentinel to users.
        label = "Live · Travelpayouts";
        pillTestId = "live-pill";
        dotInk = false;
    }

    const subtext = grounded || isSeedNeutral ? null : altDisplayLabel(alt_network_label);

    return (
        <span
            className="inline-flex flex-col items-start"
            data-source={(affiliate_network || source).toLowerCase()}
        >
            <span
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[#737373]"
                data-testid={pillTestId}
                data-network={(affiliate_network || source).toLowerCase()}
                title={
                    sia
                        ? "Singapore Airlines sandbox — synthetic UAT data, bookings will not complete."
                        : grounded
                        ? `Real-world data from ${source_label}`
                        : isSeedNeutral
                        ? "Seed inventory — Travelpayouts API is currently disabled."
                        : `Bookings route via ${label.replace(/^Live · /, "")}`
                }
            >
                <span className="relative flex h-1.5 w-1.5">
                    {!dotInk && (
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A] opacity-60" />
                    )}
                    <span
                        className="relative inline-flex h-1.5 w-1.5 rounded-full"
                        style={{ background: dotInk ? "#0A0A0A" : "#16A34A" }}
                    />
                </span>
                {label}
            </span>
            {subtext && (
                <span
                    className="mt-0.5 pl-2 text-[10px] tracking-wide text-[#A3A3A3]"
                    data-testid="alt-network-subtext"
                >
                    {subtext}
                </span>
            )}
        </span>
    );
}

/** Sized container so the glyph and dot align cleanly on the right edge. */
export function HeaderAccessory({ destinationWeather, dotColor }) {
    return (
        <div className="flex items-center gap-2" data-testid="card-header-accessory">
            {destinationWeather?.condition_key && (
                <WeatherGlyph
                    condition_key={destinationWeather.condition_key}
                    size={28}
                    title={destinationWeather.summary || destinationWeather.condition_key}
                />
            )}
            <span
                aria-hidden
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: dotColor }}
            />
        </div>
    );
}
